import moment from 'moment';
import { formatDate, getCurrencySettings, getRadix, getTranslate, isObject } from './helpers';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { IBranch, ICurrencySettings } from 'src/types/branches';
import { CoordsBounds, CoordsPaths, LatLng } from 'src/types/main';

export const roundPrice = (price: number | string, divided: number = 100): number | string => {
  const numberOfDigits = getRadix(divided);

  if (typeof price === 'number' && !isNaN(price)) return Number(price.toFixed(numberOfDigits));

  if (typeof price === 'string') {
    const _price = Number(price);
    if (typeof _price === 'number' && !isNaN(_price)) return Number(_price.toFixed(numberOfDigits));
  }

  if (!price) return 0;

  return price;
};

/**
 * Formats the cost of the product depending on the currency and currency position
 * Special characters are used for correct display currency
 *
 * @param {(string | number)} price - price number
 * @param {(string | undefined)} [currency = appStore.settings?.currency] - currecy of price
 * @param {string} [position = appStore.settings?.currency_position] - position of currency (more priority than {@link currency})
 * (default {@link appStore.settings?.currency_position})
 * @param {string} [divided = 100] - how much to round the price in currency (more priority than {@link currency})(default - 100)
 * @return {string}
 */
export const formatPrice = (
  price: string | number,
  currency: string | undefined = appStore.settings?.currency,
  position: string | undefined = appStore.settings?.currency_position,
  divided: number = 100
): string => {
  if (position === 'l') {
    return `\u202A${getTranslate(currency)}\u202C ${roundPrice(price, divided)}`;
  }
  return `${roundPrice(price, divided)} \u202A${getTranslate(currency)}\u202C`;
};

/**
 * Formats the cost of the product depending on the currency settings
 * Calls {@link formatPrice}
 *
 * @export
 * @param {(string | number)} price -  Price number
 * @param {ICurrencySettings} [currency] - Currency settigns
 * @return {*}
 */
export const formatPriceSettings = (price: string | number, currency_settings?: ICurrencySettings) => {
  if (currency_settings) {
    const { currency_code, currency_divided, currency_position } = currency_settings;

    return formatPrice(price, currency_code, currency_position, currency_divided);
  }
  return formatPrice(price);
};

/**
 * Price formatting
 * Take currency and currency position from Branch
 * If Branch is undefined, calls just formatPrice
 * Calls {@link formatPrice} and {@link getCurrencySettings}
 *
 * @export
 * @param {string} price
 * @param {IBranch} { currency, currency_position }
 * @return {*}  {string}
 */
export const formatPriceBranch = (price: string | number, branch: IBranch | undefined): string => {
  if (branch) {
    const currency_settings = getCurrencySettings(branch);
    return formatPriceSettings(price, currency_settings);
  }
  return formatPrice(price);
};

export const formatUnit = (unit_name: string | null | undefined, weight: number | string): string | null => {
  if (!unit_name || unit_name === '-') return null;
  return `${weight} ${localeStore.t(unit_name)}`;
};

export const hash = (json: string) => {
  let _hash = 0;

  for (let i = 0; i < json.length; i++) {
    const chr = json ? json.charCodeAt(i) : 0;
    _hash = (_hash << 5) - _hash + chr;
    _hash |= 0; // Convert to 32bit integer
  }

  return `${_hash}`;
};

// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
export const formatImagePath = (name: string) => `${process.env.REACT_APP_CLOUD_FRONT}/media/images/${name}`;
// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
export const formatAudioPath = (name: string) => `${process.env.REACT_APP_CLOUD_FRONT}/media/audio/${name}`;

/**
 * function to format error from backend
 *
 * @param {any} error - error returned by backend
 * @returns {string}
 */
export const getMessageFromError = (error: any): string => {
  if (!error) return localeStore.t('error_try_again');

  if (error.isAxiosError) {
    const { message: errorMessage } = error;
    const { status, statusText, data } = error.response || {};
    const { message: responseMessage, errors, exception, file } = data || {};

    if (responseMessage) return responseMessage;
    if (errorMessage) return errorMessage;

    if (typeof errors === 'object' && errors.length) {
      return String(errors);
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const customMessage: string = `Status: ${status};StatusText: ${statusText};Exception: ${exception};File: ${file}`;

    return customMessage;
  }

  return localeStore.t('request_error');
};

export const getHostName = (origin: string) => {
  const host = origin.split(/https:\/\/|http:\/\/|:|\./);
  // to handle custom domain like https://www.anoosh.sa, https://www.ktaykit.com
  if (host[1] === 'www') {
    return host[2];
  }
  return host[1];
};

export const objectMerge = <T>(main: any, data: any, allowedKeys?: string[]): T => {
  if (!isObject(data)) return main;
  Object.keys(data).forEach((key) => {
    if (allowedKeys && allowedKeys.length && !allowedKeys.includes(key)) return;
    main[key] = data[key];
  });
  return { ...main };
};

export const arrayMerge = <T>(main: T[], incoming: T[], field?: string): T[] => {
  const compare = (a: any, b: any) => (field ? a[field] === b[field] : a === b);
  return incoming.reduce((_main, i: T) => {
    const idx = _main.findIndex((_i) => compare(_i, i));
    if (~idx) {
      _main[idx] = i;
    } else {
      _main.push(i);
    }
    return _main;
  }, main);
};

export const getSocketHost = (): string => {
  if (!appStore.settings?.socket) return '';
  let host = decodeURIComponent(appStore.settings.socket);
  if (!host.includes('https:')) host = `https://${host}`;
  return host;
};

export const takerDomainPattern = /^(https?:\/\/)(.*[a-z\d])(.my.taker.io)$/;

export const formatPhone = (phone: string, split?: boolean) => {
  if (!phone || typeof phone !== 'string') return phone;

  if (split) return phone.split('+').join('');
  if (phone[0] !== '+') return `+${phone}`;
  return phone;
};

export const formatPercentage = (value: number) => {
  let _value: string = value.toString();
  if (value > 0 && value < 1000) _value = `+${value}`;
  if (value > 999) _value = `>+999`;
  return _value;
};

export const pathToBounds = (paths: CoordsPaths): CoordsBounds => paths.map((path) => [path.lat, path.lng]);
export const boundsToPath = (bounds: CoordsBounds): CoordsPaths => bounds.map((bound): LatLng => ({ lat: bound[0], lng: bound[1] }));

export const isPointInBounds = (bounds: CoordsBounds, center: LatLng): boolean => {
  let inside = false;
  for (let i = 0, j = bounds.length - 1; i < bounds.length; j = i++) {
    const xi = bounds[i][0];
    const yi = bounds[i][1];

    const xj = bounds[j][0];
    const yj = bounds[j][1];

    const intersect = yi > center.lng !== yj > center.lng && center.lat < ((xj - xi) * (center.lng - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};
export const circleToPolygon = (point: LatLng, radius: number, dir: 1 | 0): CoordsBounds => {
  const d2r = Math.PI / 180; // degrees to radians
  const r2d = 180 / Math.PI; // radians to degrees
  const earthsradius = 3963; // 3963 is the radius of the earth in miles

  const points = 32;

  // find the radius in lat/lng
  const rlat = (radius / earthsradius) * r2d;
  const rlng = rlat / Math.cos(point.lat * d2r);

  const extp: CoordsBounds = [];
  let [start, end]: [LatLng['lat'], LatLng['lng']] = [0, 0];
  if (dir === 1) {
    start = 0;
    end = points + 1; // one extra here makes sure we connect the path
  } else {
    start = points + 1;
    end = 0;
  }

  for (let i = start; dir === 1 ? i < end : i > end; i = i + dir) {
    const theta = Math.PI * (i / (points / 2));
    const ey = point.lng + rlng * Math.cos(theta); // center a + radius x * cos(theta)
    const ex = point.lat + rlat * Math.sin(theta); // center b + radius y * sin(theta)
    const _point = new google.maps.LatLng(ex, ey);
    extp.push([_point.lat(), _point.lng()]);
  }
  return extp;
};

export const numberWithDigitSeparator = (num: number): string => {
  // in Russia digit separator is a space
  return new Intl.NumberFormat('ru-RU').format(num);
};

export const capitalLetter = (str: string) => {
  if (!str || typeof str !== 'string') return str;

  const first: string = str[0].toUpperCase();
  return first + str.slice(1);
};

export const truncateLongString = (value?: string | number, lengthLimit: number = 10) => {
  if (!value) return '';
  const str = String(value);
  return str.length > lengthLimit ? `${str.slice(0, 4)}...${str.slice(-4)}` : str;
};

export const transformToHexColorString = (value: string): string => {
  if (typeof value !== 'string') return value;

  return '#' + value.replaceAll('#', '');
};

export const formatDueTime = (date: string | null): string => {
  if (moment(date).isSame(Date.now(), 'day')) {
    return localeStore.t('txt_today') + ', ' + formatDate(date, { onlyTime: true });
  } else if (moment(date).isSame(Date.now() + 86400000, 'day')) {
    return localeStore.t('txt_tomorrow') + ', ' + formatDate(date, { onlyTime: true });
  } else {
    return formatDate(date || '', {});
  }
};
